import React, { useState, useEffect, useRef, createContext, useContext } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Undo, AlertCircle, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { runWithConcurrencyLimit } from '@/utils/concurrency';

const DeleteWithUndoContext = createContext(null);

// ✅ FIX 1: Moved outside the map loop to prevent losing state on re-renders
function UndoTimer({ timestamp }) {
  const [timeLeft, setTimeLeft] = useState(30);
  
  useEffect(() => {
    const interval = setInterval(() => {
      const elapsed = Math.floor((Date.now() - timestamp) / 1000);
      const remaining = 30 - elapsed;
      setTimeLeft(remaining);
      if (remaining <= 0) {
        clearInterval(interval);
      }
    }, 1000);
    return () => clearInterval(interval);
  }, [timestamp]);
  
  return <span>{timeLeft}s</span>;
}

export function DeleteWithUndoProvider({ children }) {
  const [pendingDeletes, setPendingDeletes] = useState([]);
  const undoneIdsRef = useRef(new Set());

  const scheduleDelete = (item, entityType, actualDeleteFn, options = {}) => {
    const deleteId = Date.now();
    actualDeleteFn(item);
    
    const deleteItem = {
      id: deleteId,
      item,
      entityType,
      actualDeleteFn,
      timestamp: Date.now(),
      isDeleted: true,
      restoreData: options.restoreData,
      restoreFn: options.restoreFn,
    };
    
    setPendingDeletes(prev => [...prev, deleteItem]);
    setTimeout(() => setPendingDeletes(prev => prev.filter(d => d.id !== deleteId)), 30000);
    
    // ✅ FIX 2: Removed window.addNotification to prevent duplicate toasts
  };

  const scheduleBulkDelete = (items, entityType, actualDeleteFn, options = {}) => {
    if (items.length === 0) return;
    const deleteId = Date.now();

    if (options.bulk) {
      // actualDeleteFn performs one request for the whole batch (e.g. a
      // single bulk-archive call) instead of one request per item.
      Promise.resolve(actualDeleteFn(items)).catch((err) => {
        console.error('Bulk delete failed:', err);
        if (window.addNotification) window.addNotification('Failed to delete items', 'error');
      });
    } else {
      // Throttle instead of firing all deletes at once (items.forEach) — a
      // "select all" bulk delete on a large list was overwhelming the backend's
      // DB connection pool and crashing the whole server.
      runWithConcurrencyLimit(items, (item) => actualDeleteFn(item), 3).catch((err) => {
        console.error('Bulk delete failed:', err);
        if (window.addNotification) window.addNotification('Some items failed to delete', 'error');
      });
    }
    
    const deleteItem = {
      id: deleteId,
      items,
      entityType,
      actualDeleteFn,
      timestamp: Date.now(),
      isDeleted: true,
      restoreData: options.restoreData,
      restoreFn: options.restoreFn,
    };
    
    setPendingDeletes(prev => [...prev, deleteItem]);
    setTimeout(() => setPendingDeletes(prev => prev.filter(d => d.id !== deleteId)), 30000);
    
    // ✅ FIX 2: Removed window.addNotification to prevent duplicate toasts
  };

  const undoDelete = async (deleteId, externalRestoreFn) => {
    if (undoneIdsRef.current.has(deleteId)) return;
    const deleteItem = pendingDeletes.find(d => d.id === deleteId);
    if (!deleteItem) return;
    
    undoneIdsRef.current.add(deleteId);
    setPendingDeletes(prev => prev.filter(d => d.id !== deleteId));

    const restoreFn = deleteItem.restoreFn ?? externalRestoreFn;
    if (restoreFn) {
      try {
        const dataToRestore = deleteItem.items ?? deleteItem.item;
        await (deleteItem.restoreFn ? deleteItem.restoreFn() : restoreFn(dataToRestore));
        if (window.addNotification) {
          const count = deleteItem.items ? deleteItem.items.length : 1;
          window.addNotification(`${count} ${deleteItem.entityType}(s) restored successfully`, 'success', 3000);
        }
      } catch (err) {
        console.error('Restore failed:', err);
        if (window.addNotification) window.addNotification('Failed to restore', 'error');
      }
    }
  };

  const dismissDelete = (deleteId) => {
    setPendingDeletes(prev => prev.filter(d => d.id !== deleteId));
  };

  const value = { pendingDeletes, scheduleDelete, scheduleBulkDelete, undoDelete, dismissDelete };
  
  return (
    <DeleteWithUndoContext.Provider value={value}>
      {children}
      {/* ✅ FIX 3: Actually render the Undo Toast inside the provider so it shows up! */}
      <DeleteUndoToast 
        pendingDeletes={pendingDeletes} 
        onUndo={undoDelete} 
        onDismiss={dismissDelete} 
      />
    </DeleteWithUndoContext.Provider>
  );
}

export function useDeleteWithUndo() {
  const ctx = useContext(DeleteWithUndoContext);
  if (!ctx) throw new Error('useDeleteWithUndo must be used within DeleteWithUndoProvider');
  return ctx;
}

export function DeleteUndoToast({ pendingDeletes, onUndo, onDismiss }) {
  return (
    <div className="fixed bottom-4 left-4 z-50 space-y-2 max-w-md">
      <AnimatePresence>
        {pendingDeletes.map((deleteItem) => {
          const isBulk = !!deleteItem.items;
          const label = isBulk
            ? `${deleteItem.items.length} ${deleteItem.entityType}(s) deleted`
            : `${deleteItem.entityType} deleted`;
            
          return (
            <motion.div
              key={deleteItem.id}
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              className="bg-slate-900 text-white rounded-lg shadow-lg p-4"
            >
              <div className="flex items-center gap-3">
                <AlertCircle className="w-5 h-5 flex-shrink-0" />
                <div className="flex-1">
                  <p className="text-sm font-medium">{label}</p>
                  <p className="text-xs opacity-90">
                    Undo available for <UndoTimer timestamp={deleteItem.timestamp} />
                  </p>
                </div>
                <div className="flex gap-2">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => onUndo(deleteItem.id)}
                    className="text-white hover:bg-white/20 gap-2 h-8"
                  >
                    <Undo className="w-4 h-4" />
                    Undo
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => onDismiss(deleteItem.id)}
                    className="text-white hover:bg-white/20 h-8 w-8"
                  >
                    <X className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            </motion.div>
          );
        })}
      </AnimatePresence>
    </div>
  );
}